// Join flow for one meeting. Host (signed in) → mic check → call.
// Guest (link) → name + mic check → knock → waiting room → call. Any state can end.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, endReasonText, guestCred, type MeetingInfo, type Me } from '../api';
import { Page } from '../components/Chrome';
import { MicCheck, type MicChoice } from '../components/MicCheck';
import { chromeIntent, inAppBrowser, isAndroid } from '../inapp';
import { Call } from './Call';
import { ex, t, T } from '../tenant';

type View =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'prejoin'; host: boolean; info: MeetingInfo | null; title: string; name?: string }
  | { kind: 'waiting' }
  | { kind: 'denied' }
  | { kind: 'call'; url: string; token: string; host: boolean; title: string }
  | { kind: 'ended'; reason: string | null }
  | { kind: 'host-left'; title: string };

/** The last name this device joined with, offered again (never submitted by itself). */
const NAME_KEY = 'meet-guest-name';
const savedName = (): string | undefined => {
  try {
    return localStorage.getItem(NAME_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};
const saveName = (name: unknown) => {
  try {
    if (typeof name === 'string' && name.trim()) localStorage.setItem(NAME_KEY, name.trim());
  } catch {
    /* storage blocked: nothing to remember */
  }
};

/** `room`: joined through a workspace's standing link instead of the meeting's own link. */
export function MeetingPage({ meetingId, link, room }: { meetingId: string; link: string | null; room?: { workspaceId: string; key: string } }) {
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [mic, setMic] = useState<MicChoice>({ deviceId: undefined });
  const [camera, setCamera] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Signed-in host? Drives navigation back to the meetings page on every view.
  const [isHost, setIsHost] = useState(false);
  useEffect(() => {
    api('/api/session').then(
      () => setIsHost(true),
      () => setIsHost(false),
    );
  }, []);

  // Decide who this is: a guest who already knocked in this tab, the signed-in host, or a new guest.
  useEffect(() => {
    (async () => {
      try {
        const me = await api<Me>(`/api/m/${meetingId}/me`, { meetingId });
        if (me.status === 'ended') return setView({ kind: 'ended', reason: me.endReason });
        if (me.host) return setView({ kind: 'prejoin', host: true, info: null, title: me.title });
        if (me.state === 'waiting' && (link || room)) return setView({ kind: 'waiting' });
        if (me.state === 'waiting') return setView({kind:'prejoin',host:false,info:null,title:me.title,name:me.name});
        if (me.state === 'denied') return setView({ kind: 'denied' });
        // A returning guest (credential kept in this browser): same entry, name prefilled.
        return setView({ kind: 'prejoin', host: false, info: null, title: me.title, name: me.name });
      } catch {
        /* not host, no credential yet: fall through to the link */
      }
      if (room) {
        try {
          const r = await api<{ live: MeetingInfo | null }>(`/api/r/${room.workspaceId}/info?k=${encodeURIComponent(room.key)}`);
          if (!r.live) return setView({ kind: 'ended', reason: null });
          return setView({ kind: 'prejoin', host: false, info: r.live, title: r.live.title, name: savedName() });
        } catch (err) {
          return setView({ kind: 'invalid', message: (err as Error).message });
        }
      }
      if (!link) return setView({ kind: 'invalid', message: 'This meeting link is incomplete. Ask the host to send it again.' });
      try {
        const info = await api<MeetingInfo>(`/api/m/${meetingId}/info?k=${encodeURIComponent(link)}`);
        if (info.status === 'ended') return setView({ kind: 'ended', reason: null });
        setView({ kind: 'prejoin', host: false, info, title: info.title, name: savedName() });
      } catch (err) {
        setView({ kind: 'invalid', message: err instanceof ApiError && err.status === 404 ? 'This meeting link is not valid.' : (err as Error).message });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, link]);

  const enter = useCallback(
    async (host: boolean, title: string) => {
      try {
        const t = await api<{ url: string; token: string }>(`/api/m/${meetingId}/token`, { body: {}, meetingId });
        setView({ kind: 'call', url: t.url, token: t.token, host, title });
      } catch (err) {
        if (err instanceof ApiError && err.status === 410) setView({ kind: 'ended', reason: null });
        else setError((err as Error).message);
      }
    },
    [meetingId],
  );

  // Waiting room: poll until the host decides or the meeting ends.
  useEffect(() => {
    if (view.kind !== 'waiting') return;
    const timer = setInterval(async () => {
      try {
        const me = await api<Me>(`/api/m/${meetingId}/me`, { meetingId });
        if (me.status === 'ended') setView({ kind: 'ended', reason: me.endReason });
        else if (me.state === 'denied') setView({ kind: 'denied' });
        else if (me.state === 'admitted') await enter(false, me.title);
      } catch {
        /* transient; keep polling */
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [view.kind, meetingId, enter]);

  async function knock(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (view.kind !== 'prejoin') return;
    setError(null);
    if (view.host) return enter(true, view.title);
    const prior = guestCred.get(meetingId);
    if (prior && !link && !room) {
      try {
        const me=await api<Me>(`/api/m/${meetingId}/me`,{meetingId});
        if(me.state==='waiting')return setView({kind:'waiting'});
        return enter(false,view.title);
      } catch(err) { setError((err as Error).message); return; }
    }
    const name = new FormData(e.currentTarget).get('name');
    saveName(name);
    try {
      const r = room
        ? await api<{ cred: string; state: string }>(`/api/r/${room.workspaceId}/knock`, { body: { k: room.key, name, prior } })
        : await api<{ cred: string; state: string }>(`/api/m/${meetingId}/knock`, { body: { k: link, name, prior } });
      guestCred.set(meetingId, r.cred);
      if (r.state === 'admitted') await enter(false, view.title);
      else setView({ kind: 'waiting' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) setView({ kind: 'ended', reason: null });
      else setError((err as Error).message);
    }
  }

  if (view.kind === 'call') {
    return (
      <Call
        meetingId={meetingId}
        url={view.url}
        token={view.token}
        host={view.host}
        title={view.title}
        micDeviceId={mic.deviceId}
        micError={mic.error}
        camera={camera}
        onEnded={(reason) => setView({ kind: 'ended', reason })}
        onLeft={() => setView(view.host ? { kind: 'host-left', title: view.title } : { kind: 'ended', reason: 'left' })}
      />
    );
  }

  const inApp = inAppBrowser();
  return (
    <Page
      title={view.kind === 'prejoin' ? view.title : T.meeting}
      bar={
        isHost && (
          <a className="btn sm" href="/" data-testid="nav-meetings">
            {T.meetings}
          </a>
        )
      }
    >
      {view.kind === 'loading' && <div className="spinner" aria-label="Loading" />}
      {view.kind === 'invalid' && (
        <div className="card">
          <h1 className="h1">Cannot open this {t.meeting}</h1>
          <p>{view.message}</p>
        </div>
      )}
      {view.kind === 'ended' && (
        <div className="card" data-testid="ended">
          <h1 className="h1">{view.reason === 'left' ? `You left the ${t.meeting}` : `This ${t.meeting} has ended`}</h1>
          {room && !isHost && (
            <button className="btn primary" data-testid="back-to-room" onClick={() => location.reload()}>
              Back to the {t.workspace} page
            </button>
          )}
          {view.reason === 'left' && !isHost && !room ? (
            <button className="btn primary" data-testid="join-again" onClick={() => location.reload()}>
              Join again
            </button>
          ) : (
            <p className="muted">{view.reason && view.reason !== 'left' ? endReasonText[view.reason] : 'The link no longer works.'}</p>
          )}
          {isHost && (
            <div className="row">
              <a className="btn primary" href="/" data-testid="new-meeting">
                New {t.meeting}
              </a>
              <a className="btn" href="/">
                Back to {t.meetings}
              </a>
            </div>
          )}
        </div>
      )}
      {view.kind === 'host-left' && (
        <HostLeft
          meetingId={meetingId}
          title={view.title}
          onRejoin={() => setView({ kind: 'prejoin', host: true, info: null, title: view.title })}
          onEnded={() => setView({ kind: 'ended', reason: 'host' })}
        />
      )}
      {view.kind === 'denied' && (
        <div className="card">
          <h1 className="h1">The host did not let you in</h1>
          <p className="muted">Contact the host if you think this is a mistake.</p>
        </div>
      )}
      {view.kind === 'waiting' && (
        <div className="card" data-testid="waiting" style={{ textAlign: 'center' }}>
          <div className="spinner" />
          <h1 className="h1">Waiting for the host to let you in</h1>
          <p className="muted">You will join automatically once admitted. Keep this page open.</p>
          <p className="muted small">The link stops working when the host ends the meeting, or after 30 minutes without activity.</p>
        </div>
      )}
      {view.kind === 'prejoin' && (
        <form className="card" onSubmit={knock}>
          <h1 className="h1">{view.title}</h1>
          {view.info && <div className="muted">Hosted by {view.info.hostName}</div>}
          {inApp && (
            <div className="notice" role="note">
              {inApp === 'whatsapp' ? 'You opened this inside WhatsApp, which may block the microphone. ' : 'This in-app browser may block the microphone. '}
              {isAndroid() ? (
                <a href={chromeIntent()}>Open in Chrome</a>
              ) : (
                <span>Tap ••• or the share icon, then “Open in Safari”.</span>
              )}
            </div>
          )}
          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}
          {!view.host && (
            <div>
              <label className="label" htmlFor="name">
                Your name
              </label>
              <input id="name" name="name" className="field" maxLength={60} placeholder={`e.g. ${ex.guestName}`} autoComplete="name" defaultValue={view.name} readOnly={!link && !room && !!guestCred.get(meetingId)} required />
            </div>
          )}
          <MicCheck onChange={setMic} />
          <label className="row small">
            <input type="checkbox" checked={camera} onChange={(e) => setCamera(e.target.checked)} /> Turn on my camera
          </label>
          <button className="btn primary block" type="submit">
            {view.host ? `Start ${t.meeting}` : view.info?.waitingRoom === false ? 'Join' : 'Ask to join'}
          </button>
          {!view.host && (
            <p className="muted small" style={{ margin: 0 }}>
              The host sees your name and when you joined. No audio or video is recorded.
            </p>
          )}
        </form>
      )}
    </Page>
  );
}

/** After the host leaves, the meeting stays open for the guests until End or a timer. */
function HostLeft({ meetingId, title, onRejoin, onEnded }: { meetingId: string; title: string; onRejoin: () => void; onEnded: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="card" data-testid="host-left">
      <h1 className="h1">You left “{title}”</h1>
      <p className="muted" style={{ margin: 0 }}>
        The meeting is still open. Guests can stay and talk until you end it, or until it ends by itself after 30 minutes without activity or 10 minutes
        empty.
      </p>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      <div className="row">
        <button className="btn primary" onClick={onRejoin}>
          Rejoin
        </button>
        <button
          className="btn danger"
          disabled={busy}
          data-testid="end-after-leave"
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api(`/api/m/${meetingId}/end`, { body: {} });
              onEnded();
            } catch (err) {
              if (err instanceof ApiError && err.status === 410) onEnded();
              else setError(`The meeting did not end: ${(err as Error).message}`);
            } finally {
              setBusy(false);
            }
          }}
        >
          End meeting for everyone
        </button>
        <a className="btn ghost" href="/">
          Back to meetings
        </a>
      </div>
    </div>
  );
}
